import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Flex,
  Text,
} from '@kognitos/lattice';

/**
 * Schedule action dialog type enum.
 */
export enum EScheduleActionType {
  DELETE = 'delete',
  UPDATE = 'update',
  PAUSE = 'pause',
  RESUME = 'resume',
}

/**
 * Props for the ScheduleActionDialog component.
 */
export interface IScheduleActionDialogProps {
  type: EScheduleActionType;
  open: boolean;
  /** Callback when the dialog is opened or closed */
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * A confirmation dialog for schedule actions (delete or update).
 * Shows appropriate title, description, warning, and action button based on type.
 *
 * @param props - Component props
 * @returns JSX element containing the schedule action dialog
 */
export function ScheduleActionDialog({
  type,
  open,
  onOpenChange,
  onConfirm,
}: IScheduleActionDialogProps) {
  const { t } = useTranslation(['automation', 'common']);

  const isDelete = type === EScheduleActionType.DELETE;

  const dialogConfig = {
    [EScheduleActionType.DELETE]: {
      title: t('automation.schedule.deleteDialog.title'),
      description: t('automation.schedule.deleteDialog.description'),
      actionLabel: t('automation.schedule.deleteDialog.delete'),
      warning: null,
      testId: 'schedule-delete-dialog',
      cancelTestId: 'schedule-delete-cancel-button',
      confirmTestId: 'schedule-delete-confirm-button',
    },
    [EScheduleActionType.UPDATE]: {
      title: t('automation.schedule.action.update'),
      description: t('automation.schedule.updateDialog.description'),
      warning: t('automation.schedule.updateDialog.warning'),
      actionLabel: t('automation.schedule.updateDialog.update'),
      testId: 'schedule-update-dialog',
      cancelTestId: 'schedule-update-cancel-button',
      confirmTestId: 'schedule-update-confirm-button',
    },
    [EScheduleActionType.PAUSE]: {
      title: t('automation.schedule.pauseDialog.title'),
      description: t('automation.schedule.pauseDialog.description'),
      warning: t('automation.schedule.pauseDialog.warning'),
      actionLabel: t('automation.schedule.action.pause'),
      testId: 'schedule-pause-dialog',
      cancelTestId: 'schedule-pause-cancel-button',
      confirmTestId: 'schedule-pause-confirm-button',
    },
    [EScheduleActionType.RESUME]: {
      title: t('automation.schedule.resumeDialog.title'),
      description: t('automation.schedule.resumeDialog.description'),
      warning: t('automation.schedule.resumeDialog.warning'),
      actionLabel: t('automation.schedule.action.resume'),
      testId: 'schedule-resume-dialog',
      cancelTestId: 'schedule-resume-cancel-button',
      confirmTestId: 'schedule-resume-confirm-button',
    },
  };

  const config = dialogConfig[type];

  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={config.testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{config.title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <Flex direction="column" gap={4}>
              <Text color="muted" level="small">
                {config.description}
              </Text>
              {config.warning && (
                <Text color="muted" level="small">
                  {config.warning}
                </Text>
              )}
            </Flex>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={config.cancelTestId}>
            {t('common.action.cancel', { ns: 'common' })}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={isDelete ? 'destructive' : 'default'}
            onClick={handleConfirm}
            data-testid={config.confirmTestId}
          >
            {config.actionLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
